/**
 * Repository PRD Page
 *
 * List and manage PRDs for a repository
 */

import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Plus, Loader2 } from 'lucide-react';
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@forgenetwork/ui';
import { PRDList } from '@/components/prd';
import { useGeneratePRD } from '@/hooks/usePRD';
import { useToast } from '@/shared/hooks/useToast';

export const Route = createFileRoute('/_authenticated/repositories/$repoId/prd')({
  component: RepositoryPRDPage,
});

function RepositoryPRDPage(): React.JSX.Element {
  const { repoId } = Route.useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const generatePRD = useGeneratePRD();

  // Generate PRD using the latest completed analysis (backend finds it automatically)
  const handleGeneratePRD = (): void => {
    generatePRD.mutate(
      { repoId },
      {
        onSuccess: (data) => {
          toast({
            title: 'PRD Generated',
            description: 'Your PRD has been generated successfully.',
          });
          navigate({ to: '/prd/$prdId', params: { prdId: data.prd.id } });
        },
        onError: (error: Error) => {
          toast({
            title: 'Generation Failed',
            description: error.message || 'Failed to generate PRD. Please ensure analysis is complete.',
            variant: 'destructive',
          });
        },
      }
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate({ to: '/repositories/$repoId', params: { repoId } })}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                Product Requirements Documents
              </h1>
              <p className="text-muted-foreground mt-1">
                View and manage PRDs generated for this repository
              </p>
            </div>
          </div>
          <Button
            onClick={handleGeneratePRD}
            disabled={generatePRD.isPending}
            className="gap-2"
          >
            {generatePRD.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                Generate PRD
              </>
            )}
          </Button>
        </div>

        {/* Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>About PRDs</CardTitle>
            <CardDescription>
              Product Requirements Documents (PRDs) are generated from code analysis
              results. They provide a structured overview of features, requirements,
              and technical specifications extracted from your codebase.
            </CardDescription>
          </CardHeader>
        </Card>

        {/* PRD List */}
        <PRDList repositoryId={repoId} />
      </div>
    </div>
  );
}
